import { Users, Search, Trash2, PhoneCall, Copy, MessageCircle, Shield } from 'lucide-react';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import API_BASE_URL from '../../config';
import TrainerPermissionsModal from './TrainerPermissionsModal';

const PAGE_SIZE = 10;

const Loader = () => createPortal(
  <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
    <p className="text-white ml-4">Loading...</p>
  </div>,
  document.body
);


   const formatToDDMMYYYY = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (isNaN(date)) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

const Banner = ({ type, message, onClose }) => {
  if (!type || !message) return null;

  const base = 'fixed top-4 left-1/2 transform -translate-x-1/2 w-96 max-w-[90vw] flex items-center justify-between px-6 py-4 rounded-2xl shadow-2xl border-2 z-[9999] animate-in slide-in-from-top-2 fade-in duration-300';
  const styles = type === 'success'
    ? 'bg-emerald-500/50 backdrop-blur-xl text-white border-emerald-400/50 shadow-emerald-500/25'
    : type === 'error'
    ? 'bg-red-500/50 backdrop-blur-xl text-white border-red-400/50 shadow-red-500/25'
    : 'bg-blue-500/50 backdrop-blur-xl text-white border-blue-400/50 shadow-blue-500/25';

  return (
    <div className={`${base} ${styles}`}>
      <span className="font-medium">{message}</span>
      <button 
        onClick={onClose} 
        className="ml-4 p-1 hover:bg-white/20 rounded-full transition-all hover:scale-110"
        aria-label="Close"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};


const TrainerCard = ({ trainer, onDelete, onCopyPhone, onEditPermissions }) => {
  const getAvatarInitials = (fullName) => {
    return `${fullName?.[0] || ''}`.toUpperCase() || 'TR';
  };

  const truncatePhone = (phone) => {
    if (!phone) return '';
    return `+91 ${phone.slice(-10)}`;
  };

  return (
    <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4 hover:shadow-lg hover:border-lime-400/50 transition-all group h-32 flex flex-col justify-between">
      {/* Header: Avatar + Name */}
      <div className="flex items-start gap-3 p-2 rounded-xl transition-all group-hover:rounded-xl">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-lime-400/10 flex items-center justify-center text-lime-400 relative flex-shrink-0">
          {trainer.photoUrl ? (
            <img 
              src={trainer.photoUrl} 
              alt={`${trainer.fullName}`}
              className="w-full h-full object-cover rounded-full"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-lime-400">
              {getAvatarInitials(trainer.fullName)}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white truncate text-base">{trainer.fullName}</h3>
          <p className="text-xs text-gray-400 truncate">{truncatePhone(trainer.phoneNumber)}</p>
        </div>
      </div>
      
      {/* TRAINER BADGE: Name + Phone + Action Buttons */}
      <div className="flex items-center gap-2">
        {/* Trainer Name Badge */}
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-lime-500/20 text-lime-400 border border-lime-500/30 flex-shrink-0 whitespace-nowrap">
                  { trainer.joiningDate ?  formatToDDMMYYYY(trainer.joiningDate) : 'Joining date N/A'}
        </span>
        
        {/* Phone Badge */}
        {/* <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 flex-shrink-0 whitespace-nowrap">
          {truncatePhone(trainer.phoneNumber)}
        </span> */}
        
        {/* Actions - Flex grow to fill space */}
        <div className="flex-1 flex justify-end gap-1 ml-auto">
          <button 
            onClick={onEditPermissions}
            className="p-1.5 hover:bg-gray-500/20 rounded-md transition-all group-hover:bg-gray-500/30 flex-shrink-0"
            title="Edit Permissions"
          >
            <Shield size={14} className="text-[#6CD800]" />
          </button>
          <button 
            onClick={() => onCopyPhone(trainer.phoneNumber)}
            className="p-1.5 hover:bg-gray-500/20 rounded-md transition-all group-hover:bg-gray-500/30 flex-shrink-0"
            title="Copy phone"
          >
            <Copy size={14} className="text-gray-400" />
          </button>
          <a
            href={`tel:${trainer.phoneNumber}`}
            className="p-1.5 hover:bg-lime-400/20 rounded-md transition-all group-hover:bg-lime-400/30 flex-shrink-0"
            title="Call"
          >
            <PhoneCall size={14} className="text-lime-400" />
          </a>
          <a
            href={`https://wa.me/${trainer.phoneNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 hover:bg-green-500/20 rounded-md transition-all group-hover:bg-green-500/30 flex-shrink-0"
            title="WhatsApp"
          >
            <MessageCircle size={14} className="text-green-400" />
          </a>
          <button 
            onClick={onDelete}
            className="p-1.5 hover:bg-red-500/20 rounded-md transition-all group-hover:bg-red-500/30 flex-shrink-0"
            title="Delete trainer"
          >
            <Trash2 size={14} className="text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

const EmployeesTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({ type: null, message: '' });
  const [selectedTrainerForPermissions, setSelectedTrainerForPermissions] = useState(null);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [page, setPage] = useState(0);

  const fetchTrainers = async () => {
    const userInfo = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/gym/GetTrainers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify(userInfo.gymcode)
      });

      if (!res.ok) throw new Error('Failed to load trainers');
      const data = await res.json();

      setTrainers(data);
      setLoading(false);
      setPage(0);
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'Failed to load trainers' });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 




  useEffect(() => {
    if (!banner.type) return;
    const t = setTimeout(() => setBanner({ type: null, message: '' }), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const confirmDeleteTrainer = (trainerId) => {
    setUserToDelete(trainerId);
    setDeleteConfirmOpen(true);
  };

  const handleCopyPhone = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      setBanner({ type: 'success', message: `Copied: ${phone}` });
    } catch (err) {
      console.error('Failed to copy phone:', err);
      setBanner({ type: 'error', message: 'Failed to copy number' });
    }
  };

  // popup removed — no close handler needed

  const filteredTrainers = trainers.filter(trainer => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (trainer.fullName && trainer.fullName.toLowerCase().includes(query)) ||
      (trainer.email && trainer.email.toLowerCase().includes(query)) ||
      (trainer.phoneNumber && trainer.phoneNumber.toLowerCase().includes(query))
    );
  });

  const total = filteredTrainers.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const pagedTrainers = filteredTrainers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleDelete = async (trainerId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/gym/deleteemployee?username=${encodeURIComponent(trainerId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });
      if (!res.ok) throw new Error('Failed to delete trainer');
      await fetchTrainers();
      setBanner({ type: 'success', message: 'Trainer deleted successfully.' });
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'Error deleting trainer. Please try again.' });
    } finally {
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  


  return (
    <div className="space-y-6">
      {loading && <Loader />}
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: null, message: '' })}
      />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-2 md:mb-0">
          <Users size={24} className="text-lime-400" />
          Trainer Management
        </h1>
        
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search trainers..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl">
        <div className="p-6 pb-4">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-white">All Trainers ({total})</h3>
            
            {total > PAGE_SIZE && (
              <div className="flex items-center gap-3 bg-[#222]/50 px-4 py-2 rounded-lg border border-[#2b2b2b]">
                <button
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#2b2b2b] hover:bg-[#333] border border-[#3b3b3b] rounded-lg text-sm font-medium text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                
                <div className="text-sm text-gray-400 min-w-[120px] text-center">
                  Page <span className="font-semibold text-white">{page + 1}</span> of{' '}
                  <span className="font-semibold text-lime-400">{Math.ceil(total / PAGE_SIZE)}</span>
                </div>
                
                <button
                  className="flex items-center gap-1 px-3 py-1.5 bg-lime-400 hover:bg-lime-300 text-[#181f10] rounded-lg text-sm font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  onClick={() => setPage(p => Math.min(maxPage, p + 1))}
                  disabled={page >= maxPage}
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[50vh] overflow-y-auto">
            {pagedTrainers.length === 0 ? (
              <div className="col-span-full p-16 text-center text-gray-400 bg-[#1a1a1a] rounded-xl">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                <div>No Trainers found matching your search.</div>
                <div className="text-sm mt-1">Try adjusting your search terms.</div>
              </div>
            ) : (
              pagedTrainers.map((trainer) => (
                <TrainerCard 
                  key={trainer.id}
                  trainer={trainer}
                  onCopyPhone={handleCopyPhone}
                  onDelete={() => confirmDeleteTrainer(trainer.phoneNumber)}
                  onEditPermissions={() => {
                    setSelectedTrainerForPermissions(trainer);
                    setPermissionsModalOpen(true);
                  }}
                />
              ))
            )}
          </div>

          {total > 0 && (
            <div className="pt-6 border-t border-[#2b2b2b] text-xs text-gray-400 text-right bg-[#1a1a1a]/50 px-4 py-3 rounded-b-xl -mx-6 mt-2">
              Showing {((page * PAGE_SIZE) + 1)} to {Math.min((page + 1) * PAGE_SIZE, total)} of {total} trainers
              {total > PAGE_SIZE && ` • Page ${page + 1} of ${Math.ceil(total / PAGE_SIZE)}`}
            </div>
          )}
        </div>
      </div>

      {/* Trainer details popup removed per request */}

      {/* Delete Confirmation */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => {
              setDeleteConfirmOpen(false);
              setUserToDelete(null);
            }}
          />
          <div className="relative z-10 w-full max-w-sm rounded-xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Delete trainer?</h3>
            <p className="text-sm text-gray-300 mb-6">
              Are you sure you want to delete this trainer? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setUserToDelete(null);
                }}
                className="px-6 py-2.5 text-sm rounded-lg bg-[#222] text-gray-200 hover:bg-[#2b2b2b] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => userToDelete && handleDelete(userToDelete)}
                className="px-6 py-2.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-400 transition-all shadow-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trainer Permissions Modal */}
      <TrainerPermissionsModal
        isOpen={permissionsModalOpen}
        onClose={() => {
          setPermissionsModalOpen(false);
          setSelectedTrainerForPermissions(null);
        }}
        trainer={selectedTrainerForPermissions}
        gymCode={JSON.parse(localStorage.getItem('fitscan_user') || '{}').gymcode}
      />
    </div>
  );
};

export default EmployeesTab;
